import { cloneElement } from "react";
import { Field as ChakraField, Input, Textarea, Switch, Select, Text, IconButton, Portal, createListCollection } from "@chakra-ui/react";

/**
 * Self-contained Chakra v3 form primitives for this package -- deliberately
 * NOT importing `cyberjob_client`'s `components/jobOffer/ui/inputs.jsx`
 * (app-specific, i18next-coupled, and `react` doesn't have an equivalent).
 * Mirrors the same compound-component patterns/design tokens both apps
 * already share (confirmed identical Chakra theme tokens in both apps'
 * duplicated ScoringPanel.jsx).
 */

const stateBorderColor = (invalid) => (invalid ? "red.400" : undefined);

export const Field = ({ label, required, invalid, hint, children }) => {
    const borderColor = stateBorderColor(invalid);
    return (
        <ChakraField.Root>
            <ChakraField.Label fontSize="xs" fontWeight={600} color={invalid ? "red.600" : "gray.700"} textTransform="uppercase" letterSpacing="wider">
                {label}
                {required && <Text as="span" color={invalid ? "red.600" : "gray.400"}> *</Text>}
            </ChakraField.Label>
            {borderColor ? cloneElement(children, { borderColor }) : children}
            {hint && <ChakraField.HelperText fontSize="xs" color="gray.500">{hint}</ChakraField.HelperText>}
        </ChakraField.Root>
    );
};

// Chrome/Edge/Safari all apply the mouse wheel to a focused <input type="number">'s value
// instead of scrolling the page -- surprising and easy to trigger by accident (scrolling
// past a score field without deliberately clicking out of it first, silently changing a
// points/maxScore value). Blurring on wheel restores normal page-scroll behavior; it only
// fires for number inputs; some other type is left untouched.
const blurNumberOnWheel = (e) => {
    if (e.currentTarget.type === "number") e.currentTarget.blur();
};

export const FormInput = ({ onWheel, ...props }) => (
    <Input
        bg="white"
        borderColor="gray.200"
        color="gray.900"
        fontSize="sm"
        _placeholder={{ color: "gray.400" }}
        _focus={{ borderColor: "purple.400" }}
        onWheel={(e) => { blurNumberOnWheel(e); onWheel?.(e); }}
        {...props}
    />
);

export const FormTextarea = ({ rows = 3, ...props }) => (
    <Textarea
        rows={rows}
        bg="white"
        borderColor="gray.200"
        color="gray.900"
        fontSize="sm"
        resize="none"
        _placeholder={{ color: "gray.400" }}
        _focus={{ borderColor: "purple.400" }}
        {...props}
    />
);

export const FormSelect = ({ value, onChange, options, placeholder, disabled = false }) => {
    const collection = createListCollection({ items: options });
    const selected = value ? [value] : [];
    const isEmpty = selected.length === 0;
    return (
        <Select.Root
            collection={collection}
            value={selected}
            disabled={disabled}
            onValueChange={(e) => onChange(e.value[0] ?? "")}
        >
            <Select.HiddenSelect />
            <Select.Control>
                <Select.Trigger
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="l2"
                    h="10"
                    fontSize="sm"
                    _focus={{ borderColor: "purple.400" }}
                >
                    <Select.ValueText placeholder={placeholder} color={isEmpty ? "gray.400" : "gray.900"} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                    <Select.Indicator />
                </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
                {/*
                    Ark UI sets the dropdown's stacking via an inline `z-index: var(--z-index)` +
                    `--z-index: <token>` pair written directly on the positioner node, always using
                    the base "dropdown" token (1000) regardless of nesting -- it does not detect that
                    this Select is rendered inside a Drawer/Dialog (zIndex "modal" = 1400, "overlay" =
                    1300), so the list opens but renders BEHIND the host modal. A `zIndex` style prop
                    can't win here (inline style always beats a class), so we force the custom
                    property with an `!important` rule instead, which does beat a plain inline value.
                */}
                <style>{".scoring-ui-select-positioner{--z-index:1550!important;}"}</style>
                <Select.Positioner className="scoring-ui-select-positioner">
                    <Select.Content bg="white" borderColor="gray.200" boxShadow="0 4px 24px rgba(23,21,31,0.12)">
                        {options.map((opt) => (
                            <Select.Item item={opt} key={opt.value} color="gray.900" _hover={{ bg: "purple.50" }}>
                                {opt.label}
                                <Select.ItemIndicator />
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select.Positioner>
            </Portal>
        </Select.Root>
    );
};

export const FormSwitch = ({ label, checked, onChange, disabled = false, size = "md" }) => (
    <Switch.Root size={size} checked={checked} disabled={disabled} onCheckedChange={(e) => onChange(e.checked)}>
        <Switch.HiddenInput />
        <Switch.Control flexShrink={0} bg={checked ? "purple.500" : "gray.300"}>
            <Switch.Thumb />
        </Switch.Control>
        {label && (
            <Switch.Label flex={1} minW={0}>
                <Text fontSize="sm" color="gray.800">{label}</Text>
            </Switch.Label>
        )}
    </Switch.Root>
);

export const RemoveIconButton = ({ onClick, ariaLabel, disabled = false }) => (
    <IconButton aria-label={ariaLabel ?? "Usuń"} variant="ghost" size="sm" color="red.500" onClick={onClick} disabled={disabled}>
        ×
    </IconButton>
);
